import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Lock, Loader2, Download, Check, Scale, Save, FileText, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { generateCopyrightDoc, saveLegalDoc, listLegalDocs, deleteLegalDoc, type CopyrightDocType, type CopyrightResult, type LegalDoc } from '../../services/studioService';
import Markdown from '../chatHome/Markdown';

interface Props {
  storyboardId: string | null;
  memberProfileIds: string[];
  /** Hire the legal specialist onto the production's crew — owned by the Studio. Rejects with a human message. */
  onRecruit: (agentId: string) => Promise<void>;
}

/** Download a document's text as a .txt file (a digital copy the creator keeps). */
function downloadDoc(title: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${title.replace(/[^\w.-]+/g, '_').slice(0, 60) || 'document'}.txt`;
  a.click(); URL.revokeObjectURL(url);
}

const DOC_TYPES: Array<{ key: CopyrightDocType; label: string; desc: string }> = [
  { key: 'authorship', label: 'Proof of authorship', desc: 'Shows you created it — for registering your copyright.' },
  { key: 'license', label: 'Ownership certificate', desc: 'States that you own it, and how others may use it.' },
  { key: 'provenance', label: 'Origin & AI record', desc: 'Where it came from, with AI disclosure (the C2PA standard).' },
];

/**
 * CopyrightPanel — generate the rights paperwork for a saved video (authorship / license / provenance) via the
 * group's legal specialist. Handles the gates the server enforces: an "unlock with a plan" state for free users,
 * a "bring in a legal specialist" action when none is present. Assistive info, not legal advice.
 */
const CopyrightPanel: React.FC<Props> = ({ storyboardId, memberProfileIds, onRecruit }) => {
  const [expanded, setExpanded] = useState(false);
  const [docType, setDocType] = useState<CopyrightDocType>('authorship');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CopyrightResult | null>(null);
  const [saved, setSaved] = useState(false);
  const [savedDocs, setSavedDocs] = useState<LegalDoc[]>([]);
  const [addingAgent, setAddingAgent] = useState(false);
  const [error, setError] = useState('');

  const loadSaved = useCallback(() => {
    if (storyboardId) listLegalDocs(storyboardId).then(setSavedDocs).catch(() => {});
  }, [storyboardId]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  if (!storyboardId) {
    return <p className="text-xs text-secondary-400">Save your video first to prepare its copyright paperwork.</p>;
  }

  const docLabel = (t: string) => DOC_TYPES.find((d) => d.key === t)?.label || 'Document';

  const generate = async () => {
    setBusy(true); setError(''); setResult(null); setSaved(false);
    try { setResult(await generateCopyrightDoc(storyboardId, docType, memberProfileIds)); }
    catch { setError('Could not prepare the document. Please try again.'); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!result?.doc) return;
    try { await saveLegalDoc({ storyboardId, docType, title: docLabel(docType), content: result.doc }); setSaved(true); loadSaved(); }
    catch { setError('Could not save the document.'); }
  };

  const removeDoc = async (id: string) => {
    try { await deleteLegalDoc(id); setSavedDocs((prev) => prev.filter((d) => d.id !== id)); } catch { /* ignore */ }
  };

  const addLegalAgent = async () => {
    if (!result?.legalAgentId) return;
    setAddingAgent(true);
    try { await onRecruit(result.legalAgentId); setResult(null); }
    catch (e) { setError((e as Error)?.message || 'Could not add the legal specialist.'); }
    finally { setAddingAgent(false); }
  };

  return (
    <div>
      {/* Collapsible — a finished-video concern, tucked away until the creator wants it. */}
      <button type="button" onClick={() => setExpanded((e) => !e)} className="flex w-full items-center gap-2 text-left">
        <ShieldCheck className="h-4 w-4 text-secondary-500" />
        <span className="flex-1">
          <span className="block text-sm font-semibold text-secondary-900 dark:text-white">Protect this video</span>
          <span className="block text-[11px] text-secondary-400">Prepare copyright & ownership paperwork{savedDocs.length ? ` · ${savedDocs.length} saved` : ''}</span>
        </span>
        {expanded ? <ChevronDown className="h-4 w-4 text-secondary-400" /> : <ChevronRight className="h-4 w-4 text-secondary-400" />}
      </button>

      {!expanded ? null : (
      <div className="mt-3">
        <p className="mb-2 text-xs text-secondary-500 dark:text-secondary-400">Pick a document — a legal specialist drafts it from your video. General info, not legal advice.</p>
        <div className="space-y-1.5">
          {DOC_TYPES.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => setDocType(d.key)}
              className={`flex w-full items-start gap-2.5 rounded-xl border px-3 py-2 text-left ${docType === d.key ? 'border-secondary-900 dark:border-white bg-secondary-50 dark:bg-secondary-800/50' : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-400'}`}
            >
              <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${docType === d.key ? 'border-secondary-900 dark:border-white' : 'border-secondary-300 dark:border-secondary-600'}`}>
                {docType === d.key && <span className="h-2 w-2 rounded-full bg-secondary-900 dark:bg-white" />}
              </span>
              <span>
                <span className="block text-sm font-medium text-secondary-900 dark:text-white">{d.label}</span>
                <span className="block text-[11px] text-secondary-400">{d.desc}</span>
              </span>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={generate}
          disabled={busy}
          className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-secondary-900 dark:bg-white px-4 py-2 text-sm font-semibold text-white dark:text-secondary-900 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scale className="h-4 w-4" />} Prepare document
        </button>

      {/* Gated / result states */}
      {result?.locked === 'subscription' && (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-secondary-200 dark:border-secondary-800 bg-secondary-50/60 dark:bg-secondary-800/30 p-3">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-secondary-500" />
          <p className="text-xs text-secondary-600 dark:text-secondary-300">Copyright paperwork is a <b>subscriber</b> feature — unlock it with a plan to protect + maintain your catalogue.</p>
        </div>
      )}
      {result?.locked === 'legal_agent' && (
        <div className="mt-3 rounded-xl border border-secondary-200 dark:border-secondary-800 bg-secondary-50/60 dark:bg-secondary-800/30 p-3">
          <div className="flex items-start gap-2">
            <Scale className="mt-0.5 h-4 w-4 shrink-0 text-secondary-500" />
            <p className="text-xs text-secondary-600 dark:text-secondary-300">A <b>legal specialist</b> prepares these documents. Bring one onto your crew to continue.</p>
          </div>
          <button type="button" onClick={addLegalAgent} disabled={addingAgent} className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-secondary-900">
            {addingAgent ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Bring in a legal specialist
          </button>
        </div>
      )}
      {result?.needsTopUp && (
        <p className="mt-3 text-xs text-secondary-500 dark:text-secondary-400">You are out of credits — top up to prepare this document.</p>
      )}
      {result?.doc && (
        <div className="mt-3">
          {/* Rendered document (not raw markdown) — readable for non-technical creators. */}
          <div className="max-h-80 overflow-auto rounded-xl border border-secondary-200 dark:border-secondary-800 bg-white dark:bg-secondary-900 p-4 text-sm leading-relaxed text-secondary-800 dark:text-secondary-200">
            <Markdown content={result.doc} />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button type="button" onClick={save} disabled={saved} className="inline-flex items-center gap-1.5 rounded-lg bg-secondary-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60 dark:bg-white dark:text-secondary-900">
              {saved ? <Check className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />} {saved ? 'Saved to paperwork' : 'Save to my paperwork'}
            </button>
            <button type="button" onClick={() => downloadDoc(docLabel(docType), result.doc || '')} className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-200 dark:border-secondary-700 px-3 py-1.5 text-xs text-secondary-700 dark:text-secondary-200 hover:border-secondary-400">
              <Download className="h-3.5 w-3.5" /> Download a copy
            </button>
          </div>
        </div>
      )}

      {/* Saved paperwork for this video */}
      {savedDocs.length > 0 && (
        <div className="mt-4">
          <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-secondary-400 dark:text-secondary-500">Saved paperwork</div>
          <div className="space-y-1.5">
            {savedDocs.map((d) => (
              <div key={d.id} className="flex items-center gap-2 rounded-lg border border-secondary-200 dark:border-secondary-800 px-2.5 py-1.5">
                <FileText className="h-3.5 w-3.5 shrink-0 text-secondary-400" />
                <span className="min-w-0 flex-1 truncate text-xs text-secondary-800 dark:text-secondary-200">{d.title}</span>
                <button type="button" onClick={() => downloadDoc(d.title, d.content)} title="Export" className="p-1 text-secondary-400 hover:text-secondary-800 dark:hover:text-white"><Download className="h-3.5 w-3.5" /></button>
                <button type="button" onClick={() => removeDoc(d.id)} title="Delete" className="p-1 text-secondary-400 hover:text-secondary-800 dark:hover:text-white"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      {error && <p className="mt-2 text-xs text-secondary-500 dark:text-secondary-400">{error}</p>}
      </div>
      )}
    </div>
  );
};

export default CopyrightPanel;
